// import {
//     createAuthRequestHeaders,
//     createRequestData,
//     createRequestHeaders,
// } from '@/api/prepareRequestData'
import { detectPlatform, getErrorMessage } from '@/shared/lib'
import { deviceId, loginRouteCodespot } from '@/api/toya/apiConstantsCodespot'
import {
    refreshTokenKeyword,
    saveToLocalStorage,
    sessionTokenLsKeyword,
} from '@/shared/localStorage/localStorageRepository'
import { RequestMethod } from '@/api/apiClient'

// toya and codespot share same api scheme. toya === codespot

const baseUrl = process.env.REACT_APP_BASE_URL

export const createCodespotApiService = () => {
    const loginRequest = async (
        requestMethod: RequestMethod,
        route: string,
        authToken?: string | null,
        includeBaseUrl?: boolean,
        requestBody?: string
    ) => {
        const requestData = createRequestData(
            requestMethod,
            undefined,
            authToken,
            requestBody
        )

        try {
            const url =
                includeBaseUrl === undefined || includeBaseUrl
                    ? `${baseUrl}${route}`
                    : `${route}`

            const response = await fetch(`${url}`, requestData)
            if (!response?.ok) {
                return null
            }
            return await response.json()
        } catch (error) {
            getErrorMessage('request', error)
            throw error
        }
    }

    const signIn = async (userName: string, password: string) => {
        try {
            const loginResponse = await loginRequest(
                'POST',
                loginRouteCodespot,
                undefined,
                true,
                JSON.stringify({
                    login: userName,
                    password,
                    deviceId,
                })
            )

            if (!loginResponse) {
                return null
            }
            saveToLocalStorage(sessionTokenLsKeyword, loginResponse.token)
            saveToLocalStorage(refreshTokenKeyword, loginResponse.refreshToken)

            return loginResponse.token
        } catch (error) {
            getErrorMessage('signIn', error)
            throw error
        }
    }

    const refreshAccessToken = async () => {
        const refreshToken = localStorage.getItem(refreshTokenKeyword)

        if (!refreshToken) {
            getErrorMessage(
                'No refresh token available',
                'No refresh token available'
            )
            return null
        }

        try {
            const response = await fetch(`${baseUrl}${loginRouteCodespot}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken, deviceId }),
            })

            if (!response.ok || response.status === 400) {
                getErrorMessage(
                    'refreshAccessToken',
                    'Refresh token is invalid or expired'
                )
                localStorage.removeItem(refreshTokenKeyword)
                localStorage.removeItem(sessionTokenLsKeyword)
                return null
            }

            const data = await response.json()

            saveToLocalStorage(sessionTokenLsKeyword, data.token)
            saveToLocalStorage(refreshTokenKeyword, data.refreshToken)
            return data.token
        } catch (error) {
            getErrorMessage('refreshAccessToken', error)
            return null
        }
    }

    const createRequestData = (
        requestMethod: RequestMethod,
        profileId?: string,
        authToken?: string | null,
        requestBody?: string,
        requestHeaders?: object
    ) => {
        const requestData = {
            method: requestMethod,
            body: requestBody ? requestBody : null,
            headers: authToken
                ? createAuthRequestHeaders(authToken, profileId)
                : createRequestHeaders(requestHeaders),
        }

        return requestData
    }
    const createAuthRequestHeaders = (
        authToken: string,
        profileId?: string
    ) => {
        const authRequestHeaders = {
            'Content-type': 'application/json; charset=UTF-8',
            Authorization: `Bearer ${authToken}`,
            Platform: detectPlatform(navigator.userAgent),
            'User-Profile-Id': profileId ? profileId : 1,
        }
        return authRequestHeaders
    }

    const createRequestHeaders = (headers?: object) => {
        const requestHeaders = {
            'Content-type': 'application/json; charset=UTF-8',
            Platform: detectPlatform(navigator.userAgent),
            ...headers,
        }

        return requestHeaders
    }

    const handleResetRequest = (email: string) => email

    return {
        loginRequest,
        signIn,
        refreshAccessToken,
        createRequestData,
        handleResetRequest,
    }
}
